import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import Select, { createFilter } from 'react-select'
import AsyncSelect from 'react-select/async'
import {
  IndicatorSeparator,
  Control,
  MultiValue,
  NoOptionsMessage,
  ValueContainer,
  SingleValue,
  Placeholder,
  Menu,
  DropdownIndicator,
} from './custom-components'
import Typography from '../Typography/Typography'
import ThemeConfig from '../../theme/ThemeConfig'
import { reactSelectComponentsStyles } from './AutocompleteStyles'
import {
  styled,
  ThemeProvider,
  StyledEngineProvider,
} from '@mui/material/styles'

const PREFIX = 'Autocomplete'

const classes = {
  root: `${PREFIX}-root`,
  input: `${PREFIX}-input`,
  singleValueContainer: `${PREFIX}-singleValueContainer`,
  multiValueContainer: `${PREFIX}-multiValueContainer`,
  chip: `${PREFIX}-chip`,
  chipFocused: `${PREFIX}-chipFocused`,
  noOptionsMessage: `${PREFIX}-noOptionsMessage`,
  placeholder: `${PREFIX}-placeholder`,
  singleValue: `${PREFIX}-singleValue`,
  paper: `${PREFIX}-paper`,
  menuTop: `${PREFIX}-menuTop`,
  divider: `${PREFIX}-divider`,
  disabled: `${PREFIX}-disabled`,
}

const StyledTypography = styled(Typography)(() => ({
  [`& .${classes.root}`]: {
    flexGrow: 1,
    minWidth: 100,
    overflow: 'visible',
  },
}))

const customComponentsStyles = () => ({
  root: {
    flexGrow: 1,
    minWidth: 100,
    overflow: 'visible',
  },
  input: {
    display: 'flex',
    padding: 0,
    minHeight: 40,
    height: 'auto',
  },
})

const Autocomplete = props => {
  const classes = { ...customComponentsStyles(), ...props.selectClasses }
  const {
    placeholder = null,
    getChipLabel,
    isDisabled = false,
    isRequired = false,
    autoFocus = false,
    helperText = '',
    errorMessage = 'This field is Required!',
    isClearable = true,
    maxMenuHeight = 300,
    fieldName = '',
    closeMenuOnSelect = true,
    menuPlacement = 'auto',
  } = props

  const mapOptionsToValues = values => {
    const filteredList =
      Array.isArray(values) && !!values.length ? values.filter(i => !!i) : null

    if (!filteredList) {
      return null
    }

    if (props.optionValueKey && props.optionLabelKey) {
      return filteredList.map(val => ({
        value: val[props.optionValueKey],
        label: val[props.optionLabelKey],
        ...val,
      }))
    } else {
      throw Error(
        'need to pass optionValueKey & optionLabelKey or mapOptionsCallback',
      )
    }
  }

  const mapOptionsCallback = props.mapOptions || mapOptionsToValues
  const isMultiSelect = Object.prototype.hasOwnProperty.call(props, 'multi')
  const cacheOptions = Object.prototype.hasOwnProperty.call(
    props,
    'cacheOptions',
  )
  const loadOptionsOnClick = Object.prototype.hasOwnProperty.call(
    props,
    'deferLoadOptionsUntilClick',
  )
  const isAsync = Object.prototype.hasOwnProperty.call(props, 'lovCallback')
  const mappedOptions =
    !isAsync && props.options ? mapOptionsCallback(props.options) : []

  const [defaultOptions, setOptions] = useState(loadOptionsOnClick ? [] : true)
  const [selected, setSelected] = useState(null)

  useEffect(() => {
    const value = isEmpty(props.value) ? null : mapOptionsCallback(props.value)
    setSelected(value)
  }, [props.value]) //eslint-disable-line

  const components = {
    IndicatorSeparator,
    Control,
    MultiValue,
    NoOptionsMessage,
    ValueContainer,
    SingleValue,
    Placeholder,
    Menu,
    DropdownIndicator,
  }

  const isEmpty = value => {
    return !value || (Array.isArray(value) && !value.length)
  }

  const triggerCallback = () => {
    return (
      !cacheOptions ||
      typeof defaultOptions === 'boolean' ||
      isEmpty(defaultOptions)
    )
  }

  const handleChange = value => {
    setSelected(value)
    if (props.onChange) {
      props.onChange(value)
    } else {
      throw Error('no callback provided for onChange')
    }
  }

  const handleBlur = () => {
    if (props.onBlur) props.onBlur(selected)
  }

  const loadOptions = (inputValue = '') => {
    try {
      if (triggerCallback) {
        return props.lovCallback(inputValue).then(values => {
          const mappedValues = mapOptionsCallback(values)
          setOptions(mappedValues)
          return mappedValues
        })
      } else {
        return new Promise(resolve => {
          resolve(defaultOptions)
        })
      }
    } catch (e) {
      console.error(e)
    }
  }

  const handleOnClick = () => {
    if (loadOptionsOnClick && (isEmpty(defaultOptions) || !cacheOptions)) {
      setOptions(null)
      loadOptions()
    }
  }

  const autocomplete = () => {
    return (
      <Select
        classes={classes}
        styles={reactSelectComponentsStyles}
        components={components}
        filterOption={createFilter({ ignoreAccents: false })}
        value={selected}
        options={mappedOptions}
        onChange={handleChange}
        onBlur={handleBlur}
        isMulti={!!isMultiSelect}
        placeholder={placeholder}
        tabSelectsValue={false}
        isDisabled={isDisabled}
        isRequired={isRequired}
        autoFocus={autoFocus}
        helperText={helperText}
        errorMessage={errorMessage}
        isClearable={isClearable}
        menuPlacement={menuPlacement}
        maxMenuHeight={maxMenuHeight}
        TextFieldProps={{
          label: fieldName,
        }}
        theme={theme => ({
          ...theme,
          colors: {
            ...theme.colors,
            primary25: '#00000014',
            primary: '#00000014',
          },
        })}
        menuPortalTarget={
          props.onDrawer && document.querySelector('.MuiDrawer-root')
        }
      />
    )
  }

  const autoCompleteAsync = () => {
    return (
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={ThemeConfig}>
          <AsyncSelect
            classes={classes}
            styles={reactSelectComponentsStyles}
            components={components}
            filterOption={createFilter({ ignoreAccents: false })}
            value={selected}
            loadOptions={loadOptions}
            defaultOptions={defaultOptions}
            onClick={handleOnClick}
            onChange={handleChange}
            onBlur={handleBlur}
            isMulti={!!isMultiSelect}
            isLoading={!isDisabled}
            placeholder={placeholder}
            getChipLabel={getChipLabel}
            isDisabled={isDisabled}
            menuPlacement={menuPlacement}
            isRequired={isRequired}
            autoFocus={autoFocus}
            helperText={helperText}
            errorMessage={errorMessage}
            isClearable={isClearable}
            closeMenuOnSelect={closeMenuOnSelect}
            maxMenuHeight={maxMenuHeight}
            TextFieldProps={{
              label: fieldName,
            }}
            theme={theme => ({
              ...theme,
              colors: {
                ...theme.colors,
                primary25: '#00000014',
                primary: '#00000014',
              },
            })}
            menuPortalTarget={
              props.onDrawer && document.querySelector('.MuiDrawer-root')
            }
          />
        </ThemeProvider>
      </StyledEngineProvider>
    )
  }

  return (
    <StyledTypography component={'div'} variant="body1">
      {isAsync ? autoCompleteAsync() : autocomplete()}
    </StyledTypography>
  )
}

Autocomplete.propTypes = {
  lovCallback: PropTypes.func,
  optionValueKey: PropTypes.string,
  optionLabelKey: PropTypes.string,
  fieldName: PropTypes.string,
  closeMenuOnSelect: PropTypes.bool,
  helperText: PropTypes.string,
  isRequired: PropTypes.bool,
  isDisabled: PropTypes.bool,
  autoFocus: PropTypes.bool,
  isClearable: PropTypes.bool,
  maxMenuHeight: PropTypes.number,
  mapOptions: PropTypes.func,
  getChipLabel: PropTypes.func,
  onBlur: PropTypes.func,
  onChange: PropTypes.func.isRequired,
  value: PropTypes.array,
}

export default Autocomplete
